import { useState } from 'react'
import { Link } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faRocket,
    faCheck,
    faArrowRight,
    faRotateLeft,
    faTrophy,
    faSpinner,
} from '@fortawesome/free-solid-svg-icons'
import useAxiosPrivate from '../../../hooks/useAxiosPrivate'
import {
    toggleQuickStartStep as apiToggleStep,
    resetQuickStartProgress as apiResetProgress,
} from '../api/knowledgeBaseApi'
import type { KbQuickStartStep } from '../types'
import { resolveIcon } from '../utils/iconMap'

interface QuickStartGuideProps {
    steps: KbQuickStartStep[]
}

const QuickStartGuide = ({ steps: initialSteps }: QuickStartGuideProps) => {
    const axiosPrivate = useAxiosPrivate()
    const [steps, setSteps] = useState(initialSteps)
    const [expanded, setExpanded] = useState(true)
    const [loadingStepId, setLoadingStepId] = useState<number | null>(null)
    const [resetting, setResetting] = useState(false)

    const completedCount = steps.filter((s) => s.completed).length
    const progress = Math.round((completedCount / steps.length) * 100)
    const allDone = completedCount === steps.length

    const toggleStep = async (stepId: number) => {
        setLoadingStepId(stepId)
        try {
            const result = await apiToggleStep(axiosPrivate, stepId)
            setSteps((prev) =>
                prev.map((s) =>
                    s.id === stepId ? { ...s, completed: result.completed } : s
                )
            )
        } catch {
            // no-op
        } finally {
            setLoadingStepId(null)
        }
    }

    const resetProgress = async () => {
        setResetting(true)
        try {
            await apiResetProgress(axiosPrivate)
            setSteps((prev) => prev.map((s) => ({ ...s, completed: false })))
        } catch {
            // no-op
        } finally {
            setResetting(false)
        }
    }

    return (
        <section className="kb-quickstart">
            <div className="kb-quickstart__header">
                <div className="kb-quickstart__header-left">
                    <div className="kb-quickstart__icon">
                        <FontAwesomeIcon
                            icon={allDone ? faTrophy : faRocket}
                        />
                    </div>
                    <div>
                        <h2 className="kb-quickstart__title">
                            {allDone ? 'All Done!' : 'Quick Start Guide'}
                        </h2>
                        <p className="kb-quickstart__subtitle">
                            {allDone
                                ? "You've completed all the steps"
                                : `${completedCount} of ${steps.length} steps completed`}
                        </p>
                    </div>
                </div>
                <div className="kb-quickstart__header-right">
                    {completedCount > 0 && (
                        <button
                            className="kb-quickstart__reset"
                            onClick={resetProgress}
                            title="Reset progress"
                            disabled={resetting}
                        >
                            <FontAwesomeIcon
                                icon={resetting ? faSpinner : faRotateLeft}
                                spin={resetting}
                            />
                        </button>
                    )}
                    <button
                        className="kb-quickstart__toggle"
                        onClick={() => setExpanded(!expanded)}
                    >
                        {expanded ? 'Collapse' : 'Expand'}
                    </button>
                </div>
            </div>

            <div className="kb-quickstart__progress">
                <div className="kb-quickstart__progress-bar">
                    <div
                        className={`kb-quickstart__progress-fill ${allDone ? 'kb-quickstart__progress-fill--done' : ''}`}
                        style={{ width: `${progress}%` }}
                    />
                </div>
                <span className="kb-quickstart__progress-text">
                    {progress}%
                </span>
            </div>

            {expanded && (
                <div className="kb-quickstart__steps">
                    {steps.map((step, idx) => {
                        const done = step.completed
                        const isLoading = loadingStepId === step.id
                        return (
                            <div
                                key={step.id}
                                className={`kb-quickstart__step ${done ? 'kb-quickstart__step--done' : ''}`}
                                style={
                                    {
                                        '--step-index': idx,
                                    } as React.CSSProperties
                                }
                            >
                                <button
                                    className="kb-quickstart__step-check"
                                    onClick={() => toggleStep(step.id)}
                                    disabled={isLoading}
                                    title={
                                        done
                                            ? 'Mark as incomplete'
                                            : 'Mark as complete'
                                    }
                                >
                                    {isLoading ? (
                                        <FontAwesomeIcon
                                            icon={faSpinner}
                                            spin
                                        />
                                    ) : done ? (
                                        <FontAwesomeIcon icon={faCheck} />
                                    ) : (
                                        <span className="kb-quickstart__step-number">
                                            {idx + 1}
                                        </span>
                                    )}
                                </button>
                                <div className="kb-quickstart__step-content">
                                    <div className="kb-quickstart__step-icon">
                                        <FontAwesomeIcon
                                            icon={resolveIcon(step.icon)}
                                        />
                                    </div>
                                    <div className="kb-quickstart__step-text">
                                        <h4
                                            className={
                                                done
                                                    ? 'text-decoration-line-through opacity-50'
                                                    : ''
                                            }
                                        >
                                            {step.title}
                                        </h4>
                                        <p>{step.description}</p>
                                    </div>
                                </div>
                                <Link
                                    to={step.link}
                                    className="kb-quickstart__step-link"
                                    title={`Go to ${step.title}`}
                                >
                                    <FontAwesomeIcon icon={faArrowRight} />
                                </Link>
                            </div>
                        )
                    })}
                </div>
            )}
        </section>
    )
}

export default QuickStartGuide
